import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageTitle, Panel, Label } from "@/pages/admin/AdminLayout";
import { useAuth } from "@/pages/admin/AdminAuth";

const LIMITS = [["max_messages_per_conversation", "Mensagens por conversa"], ["max_conversations_per_ip_day", "Conversas por IP por dia"], ["monthly_cost_limit_eur", "Limite de custo mensal (€)"], ["price_in_per_m_eur", "Custo entrada (€ / 1M tokens)"], ["price_out_per_m_eur", "Custo saída (€ / 1M tokens)"]];

function SuggestionsEditor({ items, onChange }) {
  const upd = (i, k, v) => onChange(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const move = (i, d) => { const c = [...items]; [c[i], c[i + d]] = [c[i + d], c[i]]; onChange(c); };
  return (
    <Panel className="space-y-4" data-testid="ai-suggestions-editor">
      <div><h3 className="text-lg">Perguntas sugeridas</h3><p className="text-xs text-[#A39E93] mt-1">Botões mostrados por cima da caixa de escrever do consultor (máx. 8). O tipo "Tamanho" mostra as opções 36–38, 39–41 e 42 mm +.</p></div>
      {items.map((x, i) => (
        <div key={x.id} className="border border-[#282520] p-3 space-y-2" data-testid={`ai-suggestion-${i}`}>
          <div className="grid md:grid-cols-[1fr_1fr_140px] gap-2">
            <input className="afield" placeholder="Botão (PT)" value={x.label_pt} onChange={(e) => upd(i, "label_pt", e.target.value)} maxLength={60} data-testid={`ai-suggestion-${i}-label-pt`} />
            <input className="afield" placeholder="Button (EN)" value={x.label_en} onChange={(e) => upd(i, "label_en", e.target.value)} maxLength={60} data-testid={`ai-suggestion-${i}-label-en`} />
            <select className="afield" value={x.kind} onChange={(e) => upd(i, "kind", e.target.value)} data-testid={`ai-suggestion-${i}-kind`}><option value="prompt">Pergunta</option><option value="size">Tamanho</option></select>
          </div>
          {x.kind === "prompt" && <div className="grid md:grid-cols-2 gap-2">
            <input className="afield" placeholder="Pergunta enviada (PT)" value={x.prompt_pt} onChange={(e) => upd(i, "prompt_pt", e.target.value)} maxLength={300} data-testid={`ai-suggestion-${i}-prompt-pt`} />
            <input className="afield" placeholder="Question sent (EN)" value={x.prompt_en} onChange={(e) => upd(i, "prompt_en", e.target.value)} maxLength={300} data-testid={`ai-suggestion-${i}-prompt-en`} />
          </div>}
          <div className="flex gap-3 text-xs">
            <button type="button" disabled={!i} onClick={() => move(i, -1)} className="text-[#A39E93] hover:text-[#E6D5AC] disabled:opacity-30" data-testid={`ai-suggestion-${i}-up`}>Subir</button>
            <button type="button" disabled={i === items.length - 1} onClick={() => move(i, 1)} className="text-[#A39E93] hover:text-[#E6D5AC] disabled:opacity-30" data-testid={`ai-suggestion-${i}-down`}>Descer</button>
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-[#e08f7f] hover:underline" data-testid={`ai-suggestion-${i}-remove`}>Remover</button>
          </div>
        </div>
      ))}
      {items.length < 8 && <button type="button" className="btn-line !py-2 text-xs" onClick={() => onChange([...items, { id: `s${Date.now()}`, kind: "prompt", label_pt: "", label_en: "", prompt_pt: "", prompt_en: "" }])} data-testid="ai-suggestion-add">Adicionar pergunta</button>}
    </Panel>
  );
}

function AiSettings() {
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/ai").then((r) => setA(r.data)); }, []);
  if (!a) return null;
  const set = (k) => (e) => setA({ ...a, [k]: e.target.value });
  const save = async (body = a) => {
    const payload = { ...body };
    LIMITS.forEach(([k]) => { payload[k] = Number(payload[k]); });
    try { setA((await api.put("/admin/ai", payload)).data); toast.success("Consultor atualizado"); } catch (e) { toast.error(errMsg(e)); }
  };
  const area = (k, l, h = 90) => <label key={k} className="block"><Label>{l}</Label><textarea className="afield" style={{ minHeight: h }} value={a[k]} onChange={set(k)} data-testid={`ai-field-${k}`} /></label>;
  return (
    <div className="space-y-6" data-testid="ai-settings">
      <Panel className="flex items-center justify-between gap-4">
        <div><p className="text-lg">Consultor ativo no site</p><p className="text-xs text-[#A39E93] mt-1">Modelo: GPT 6 Luna{isSuper && a.month_cost_eur != null ? ` · custo este mês: ${a.month_cost_eur.toFixed(4)} €` : ""}</p></div>
        <Switch checked={a.enabled} onCheckedChange={async (v) => { setA((x) => ({ ...x, enabled: v })); try { await api.put("/admin/ai", { enabled: v }); toast.success(v ? "Consultor ligado" : "Consultor desligado"); } catch (e) { toast.error(errMsg(e)); } }} data-testid="ai-enabled-switch" />
      </Panel>
      <Panel className="space-y-4">
        <h3 className="text-lg">Textos obrigatórios (editáveis, não removíveis)</h3>
        <div className="grid md:grid-cols-2 gap-4">{area("notice_pt", "Aviso de IA (PT)")}{area("notice_en", "AI notice (EN)")}{area("welcome_pt", "Boas-vindas (PT)", 70)}{area("welcome_en", "Welcome (EN)", 70)}</div>
      </Panel>
      <Panel className="space-y-4">
        <h3 className="text-lg">Base de conhecimento, tom e regras</h3>
        <div className="grid md:grid-cols-2 gap-4">{area("kb_pt", "Base de conhecimento (PT)", 180)}{area("kb_en", "Knowledge base (EN)", 180)}</div>
        {area("tone", "Tom", 60)}{area("extra_rules", "Regras extra", 80)}
      </Panel>
      <SuggestionsEditor items={a.suggestions || []} onChange={(s) => setA({ ...a, suggestions: s })} />
      <Panel>
        <h3 className="text-lg mb-4">Limites</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">{LIMITS.map(([k, l]) => <label key={k}><Label>{l}</Label><input type="number" step="any" min="0" className="afield" value={a[k]} onChange={set(k)} data-testid={`ai-field-${k}`} /></label>)}</div>
      </Panel>
      <button className="btn-gold" onClick={() => save()} data-testid="ai-save-button">Guardar alterações</button>
    </div>
  );
}

function Conversations() {
  const [sp, setSp] = useSearchParams();
  const [d, setD] = useState(null);
  const [sel, setSel] = useState(null);
  const [erasures, setErasures] = useState([]);
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  const cid = sp.get("conversa");
  useEffect(() => { api.get("/admin/conversations").then((r) => setD(r.data)); api.get("/admin/erasure-requests").then((r) => setErasures(r.data)); }, []);
  useEffect(() => { if (cid) api.get(`/admin/conversations/${cid}`).then((r) => setSel(r.data)); else setSel(null); }, [cid]);
  if (!d) return null;
  const done = async (id) => { await api.patch(`/admin/erasure-requests/${id}`); setErasures(erasures.map((e) => (e.id === id ? { ...e, status: "done" } : e))); };
  const removeConv = async (id, e) => {
    e?.stopPropagation(); e?.preventDefault();
    if (!window.confirm("Eliminar esta conversa definitivamente?")) return;
    try {
      await api.delete(`/admin/conversations/${id}`);
      setD((prev) => ({ ...prev, items: prev.items.filter((x) => x.id !== id), total: Math.max(0, prev.total - 1) }));
      if (cid === id) setSp({});
      toast.success("Conversa eliminada");
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="space-y-6" data-testid="ai-conversations">
      <div className="grid grid-cols-3 gap-4">
        {[["Conversas", d.total], ["Com pedido", d.converted], ["Taxa de conversão", `${d.conversion_rate}%`]].map(([l, v]) => <Panel key={l}><Label>{l}</Label><p className="font-display text-3xl text-[#E6D5AC]" data-testid={`conv-stat-${l}`}>{v}</p></Panel>)}
      </div>
      {erasures.some((e) => e.status === "open") && (
        <Panel className="border-[#e0b97f]/40" data-testid="erasure-requests">
          <h3 className="text-lg mb-3">Pedidos de apagamento de dados</h3>
          {erasures.filter((e) => e.status === "open").map((e) => <div key={e.id} className="flex items-center gap-3 text-sm py-2 border-t border-[#282520]"><span className="text-[#A39E93]">{fmtDate(e.created_at)}</span><span className="flex-1">{e.details || "—"}</span><button className="text-xs text-[#C8AE6E]" onClick={() => setSp({ conversa: e.conversation_id })}>Conversa</button><button className="btn-line !py-1 !px-3 text-xs" onClick={() => done(e.id)} data-testid={`erasure-done-${e.id}`}>Tratado</button></div>)}
        </Panel>
      )}
      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-6">
        <div className="border border-[#282520] divide-y divide-[#282520] max-h-[640px] overflow-y-auto">
          {d.items.map((c) => (
            <div key={c.id} className={`relative group ${cid === c.id ? "bg-[#C8AE6E]/10" : ""}`}>
              <button onClick={() => setSp({ conversa: c.id })} className="w-full text-left px-4 py-3 hover:bg-white/[0.03]" data-testid={`conversation-row-${c.id}`}>
                <div className="flex justify-between text-xs text-[#A39E93]"><span>{fmtDate(c.created_at)} · {c.lang.toUpperCase()} · {c.msg_count} msg</span>{c.converted && <span className="text-[#9fc79a]">Pedido</span>}</div>
                <p className="text-sm mt-1 truncate pr-7">{c.preview || <span className="text-[#A39E93]">(sem mensagens)</span>}</p>
              </button>
              {isSuper && (
                <button type="button" onClick={(e) => removeConv(c.id, e)} className="absolute top-2 right-2 p-1.5 text-[#A39E93] hover:text-[#e0937f] opacity-0 group-hover:opacity-100 focus:opacity-100" title="Eliminar conversa" data-testid={`conversation-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </div>
          ))}
          {d.items.length === 0 && <p className="p-6 text-sm text-[#A39E93]">Sem conversas.</p>}
        </div>
        {sel && (
          <Panel className="max-h-[640px] overflow-y-auto space-y-3" data-testid="conversation-detail">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-[#A39E93] flex-1">Aviso de IA mostrado em {fmtDate(sel.ai_notice_at)} · Tratamento: {sel.salutation || "—"} · Contacto: {sel.contact_status || "—"} · Custo: {(sel.cost_eur || 0).toFixed(4)} €</p>
              {isSuper && <button type="button" onClick={(e) => removeConv(sel.id, e)} className="btn-line !py-1.5 !px-3 text-xs !border-[#e0937f]/40 !text-[#e0937f]" data-testid="conversation-delete-detail"><Trash2 className="w-3.5 h-3.5" /> Eliminar</button>}
            </div>
            {sel.display.map((m, i) => <div key={i} className={`text-sm p-3 whitespace-pre-wrap ${m.role === "user" ? "bg-[#C8AE6E]/10 ml-8" : m.role === "notice" ? "border border-[#282520] text-[#A39E93] text-xs" : m.role === "action" ? "border-l-2 border-[#C8AE6E] text-[#E6D5AC] text-xs !py-1.5" : "bg-[#0C0B09] mr-8"}`}>{m.text.replace(/\[cards:([^\]]*)\]/g, "↳ Cartões mostrados: $1").split(/(\*\*[^*]+\*\*|https?:\/\/[^\s]+)/g).map((p, j) => (p.startsWith("**") && p.endsWith("**") ? <strong key={j} className="text-[#E6D5AC] font-medium">{p.slice(2, -2)}</strong> : /^https?:\/\//.test(p) ? <a key={j} href={p} target="_blank" rel="noopener noreferrer" className="text-[#C8AE6E] underline break-all" data-testid="conversation-shown-link">{p}</a> : p))}</div>)}
          </Panel>
        )}
      </div>
    </div>
  );
}

export default function ConsultantAdmin() {
  return (
    <div data-testid="admin-consultant">
      <PageTitle title="Consultor de IA" />
      <Tabs defaultValue="conv">
        <TabsList className="bg-[#161512] mb-6"><TabsTrigger value="conv" data-testid="ai-tab-conversations">Conversas</TabsTrigger><TabsTrigger value="settings" data-testid="ai-tab-settings">Definições</TabsTrigger></TabsList>
        <TabsContent value="settings"><AiSettings /></TabsContent>
        <TabsContent value="conv"><Conversations /></TabsContent>
      </Tabs>
    </div>
  );
}
